import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Delete, ShieldCheck } from "lucide-react";

export function PinPad({ value, onChange, max = 6, disabled, dark = true }) {
  const press = (d) => { if (!disabled && value.length < max) onChange(value + d); };
  const back = () => { if (!disabled) onChange(value.slice(0, -1)); };
  return (
    <div data-testid="pin-pad" className="w-full max-w-[280px] mx-auto">
      <div className="flex justify-center gap-2 mb-5">
        {Array.from({ length: max }).map((_, i) => (
          <span key={i} className={`w-3.5 h-3.5 rounded-full transition-all ${i < value.length ? (dark ? "bg-[#C69C4A]" : "bg-[#123026]") : (dark ? "bg-[#1B3028]" : "bg-[#EAE5DC]")}`} />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2.5">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button key={d} type="button" data-testid={`pin-key-${d}`} onClick={() => press(d)}
            className={`h-14 rounded-2xl text-xl font-bold active:scale-95 transition-all ${dark ? "bg-[#111F1A] border border-[#1B3028] text-stone-100 hover:border-[#C69C4A]/50" : "bg-white border border-[#EAE5DC] text-[#123026] hover:bg-[#F5F1E8]"}`}>{d}</button>
        ))}
        <span />
        <button type="button" data-testid="pin-key-0" onClick={() => press("0")}
          className={`h-14 rounded-2xl text-xl font-bold active:scale-95 transition-all ${dark ? "bg-[#111F1A] border border-[#1B3028] text-stone-100 hover:border-[#C69C4A]/50" : "bg-white border border-[#EAE5DC] text-[#123026] hover:bg-[#F5F1E8]"}`}>0</button>
        <button type="button" data-testid="pin-key-back" onClick={back}
          className={`h-14 rounded-2xl grid place-items-center active:scale-95 transition-all ${dark ? "bg-[#111F1A] border border-[#1B3028] text-stone-400 hover:text-stone-100" : "bg-white border border-[#EAE5DC] text-[#8A918D]"}`}><Delete className="w-5 h-5" /></button>
      </div>
    </div>
  );
}

// Step-up confirmation for a sensitive action. Verifies the PIN server-side
// (Manager/Owner only) before running `onConfirmed(authorizedBy)`.
export function PinConfirmDialog({ open, onClose, slug, title = "Autorisation requise", description, onConfirmed }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [pinLen, setPinLen] = useState(4);

  useEffect(() => { if (open && slug) api.getRestaurant(slug).then((r) => setPinLen(r?.pin_length || 4)).catch(() => {}); }, [open, slug]);

  const reset = () => { setPin(""); setError(""); setLoading(false); };
  const close = () => { reset(); onClose(); };

  const submit = async () => {
    if (pin.length !== pinLen) { setError(`PIN à ${pinLen} chiffres`); return; }
    setLoading(true); setError("");
    try {
      const res = await api.verifyPin(slug, pin);
      const usedPin = pin;
      reset();
      onConfirmed(res.authorized_by, usedPin);
    } catch (e) {
      setError(e?.response?.data?.detail || "PIN non reconnu ou droits insuffisants");
      setPin("");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="pin-confirm-dialog" className="max-w-xs rounded-3xl bg-[#0C1713] border-[#1B3028] text-stone-100">
        <DialogTitle className="text-lg flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#C69C4A]" /> {title}</DialogTitle>
        <DialogDescription className="text-stone-400 text-xs">{description || "Saisissez le PIN Manager/Propriétaire pour confirmer cette action sensible."}</DialogDescription>
        <div className="pt-2">
          <PinPad value={pin} onChange={setPin} max={pinLen} disabled={loading} dark />
          {error && <p data-testid="pin-confirm-error" className="text-center text-sm text-red-400 mt-3">{error}</p>}
          <button data-testid="pin-confirm-submit" onClick={submit} disabled={loading || pin.length !== pinLen}
            className="mt-4 w-full h-12 rounded-full bg-[#C69C4A] hover:bg-[#B38B3A] text-[#0C1713] font-bold disabled:opacity-40">
            {loading ? "Vérification…" : "Confirmer"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
